/* eslint-disable @nx/enforce-module-boundaries */
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Form, Surface, Txt } from '@vst/beam';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectHouseNumber,
  selectIsServiceabilityLoading,
  selectServiceAddressValues,
  serviceabilityActions,
} from '@buy-viasat/redux/src/serviceability';
import { appActions, CustomerType, selectCustomerType, selectLocale } from '@buy-viasat/redux/src/app';
import { Divider, Navigator } from '@buy-viasat/react';
import { adobeTargetDataLayer } from '@buy-viasat/utils';
import { DataLayerEvents } from '@buy-viasat/redux/src/analytics';
import { planActions } from '@buy-viasat/redux/src/plan';
import MultiFieldAddress from '../MultiFieldAddress';

import {
  ScrubbedAddress,
  useScrubAddress,
} from '../../../../../packages/client/src/shared/utils/dataFetchingHooks/useScrubAddress';
import { AddressType } from '@buy-viasat/redux/src/address';
import { USE_NEW_API } from '../../../../../packages/client/src/shared/utils/dataFetchingHooks/constants';
import { selectSpbBillingAccountId } from '@buy-viasat/redux/src/payment-information';

interface FormAddressProps {
  onApartmentUnitProvided: () => void;
  isApartmentUnitRequirementMet: boolean;
}

const FormAddress = ({ onApartmentUnitProvided, isApartmentUnitRequirementMet }: FormAddressProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation(['serviceability']);

  const billingAccount = useSelector(selectSpbBillingAccountId);
  const locale = useSelector(selectLocale);
  const customerType = useSelector(selectCustomerType);
  const { houseNumber } = useSelector(selectHouseNumber);
  const streetAddress = useSelector(selectServiceAddressValues);
  const isServiceabilityLoading = useSelector(selectIsServiceabilityLoading);
  const isBusinessUser = customerType === CustomerType.B;

  const address: AddressType = useSelector(selectServiceAddressValues);

  const { scrubAddress } = useScrubAddress<ScrubbedAddress>();

  const handleServiceabilitySubmit = useCallback(async () => {
    if (USE_NEW_API) {
      scrubAddress({ address });
    } else {
      dispatch(serviceabilityActions.scrubServiceAddress()); // This triggers the saga
    }
    dispatch(planActions.setAvailableProducts([]));
  }, [address, dispatch, scrubAddress]);

  const handleConfirmButtonClick = useCallback(() => {
    adobeTargetDataLayer.push({
      event: DataLayerEvents.AA_EVENT,
      eventData: {
        event: 'findPlans',
      },
    });
  }, [billingAccount, dispatch]);

  const handleBackButtonClick = () => {
    const newCustomerType = customerType === CustomerType.B ? CustomerType.R : CustomerType.B;
    navigate(`/${locale}/${newCustomerType}/`);

    dispatch(appActions.setSmbLinkResidentialRedirect(true));
    dispatch(appActions.resetPartyId());
  };

  const backButtonText = isBusinessUser
    ? t('serviceability.internetPlansLink')
    : t('serviceability.businessInternetPlanLink');

  const messagesArraySpinner = [t('fiber.checkingExistingServices'), t('fiber.checkingAvailablePlans')];
  const queryParams = new URLSearchParams(window.location.search);

  return (
    <Form
      name="serviceabilityForm"
      data-testid="serviceabilityForm"
      onSubmit={handleServiceabilitySubmit}
      initialValues={{
        streetAddress:
          queryParams.get('addressLine1') ??
          (houseNumber
            ? streetAddress.addressLines[0].split(houseNumber)[0]
            : streetAddress.addressLines[0].split(',')[0]),
        aptAddress: queryParams.get('addressLine2') ?? streetAddress.addressLines[1],
        postalCodeAddress: queryParams.get('postalCode') ?? streetAddress.postalCode,
        houseNumber: houseNumber ?? '',
      }}
    >
      <Surface radius="16px" px={{ xs: '16px', md: '48px' }} pt={{ xs: '24px', md: '48px' }}>
        <Txt variant="heading6" data-testid="serviceabilityHeadlineSubtitle">
          {isBusinessUser ? t('serviceability.headlineSmb.subtitle') : t('serviceability.headline.subtitle')}
        </Txt>
        <Divider my={{ xs: '24px', md: '40px' }} />
      </Surface>

      <Surface px={{ md: '24px' }} mt="40px" mb={{ xs: '16px', md: '24px' }}>
        <MultiFieldAddress
          isApartmentUnitRequirementMet={isApartmentUnitRequirementMet}
          onApartmentUnitProvided={onApartmentUnitProvided}
        />
      </Surface>

      <Divider />

      <Navigator
        onConfirmButtonClick={handleConfirmButtonClick}
        disabled={false}
        loading={isServiceabilityLoading}
        onBackButtonClick={handleBackButtonClick}
        backButtonHref={isBusinessUser ? `/${locale}/${CustomerType.R}/` : `/${locale}/${CustomerType.B}/`}
        backButtonText={backButtonText}
        loadingText={messagesArraySpinner}
        confirmButtonText={t('serviceability.button.findPlans.label')}
        showLoadingOutsideButton={true}
      />
    </Form>
  );
};

export default FormAddress;
